import { useEffect } from 'react';
import { useScript } from 'usehooks-ts';
import { useCustomPreferences } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { getExtendProductId } from '../../utils/product-utils';
import { isClient, isServer } from '../../utils/utils';
import { fontFace } from './helper';
import type {
    Basket,
    NewHardGoodItemWithExtend,
    NewProductItem,
    ProductItem,
} from '../../bff-api/baskets/model';
import type { Product } from '../../bff-api/products/model';
import type { ExtendModalOnClose, ExtendPlan } from '../../types/extend';

export const EXTEND_COMPONENT_ID = 'extend-offer';

const EXTEND_HEAD_ID = 'pwa-pdp-extend-styles';

const EXTEND_IFRAME = `#${EXTEND_COMPONENT_ID} iframe`;

let extendCSS = '';

let isAvailable = false;

interface ExtendWarrantyContext {
    isPDPEnabled: boolean;
    isPDPUpSellEnabled: boolean;
    isCartUpsellEnabled: boolean;
    isAvailable: boolean;
    addPlan: (
        item: ProductItem | NewProductItem,
        plan?: ExtendPlan,
    ) => NewHardGoodItemWithExtend | undefined;
    getPlan: () => ExtendPlan | undefined;
    buildExtendProductFromProductItem: (basket: Basket, item: NewProductItem) => Product | null;
    buildExtendProductFromProduct: (product: Product) => Product;
    init: (productId: string) => void;
    openModal: (productId: string, onClose: ExtendModalOnClose) => void;
    updateWidgetStyle: () => void;
    isSdkLoaded: boolean;
}

const init = (productId: string): void => {
    if (isServer) {
        return;
    }

    if (!extendCSS) {
        extendCSS = `<style type="text/css" id='${EXTEND_HEAD_ID}'>
            ${fontFace()}

            .caption {
              display: flex !important;
              align-items: center !important;
              margin-bottom: 0 !important;
            }

            .caption-text, .term-length, .plan-price {
              font-family: 'TT Norms Pro' !important;
              letter-spacing: 0.5px !important;
              font-size: 16px !important;
              font-weight: 400 !important;
              line-height: 24px !important;
              letter-spacing: 0.5px !important;
              color: #414042 !important;
            }

            .logo {
              width: 56px !important;
              height: 100% !important;
            }

            .info-button {
              cursor: pointer !important;
            }

            .button-group {
              display: none;
              gap: 8px !important;
              margin-top: 16px !important;

              .btn-offer {
                padding: 8px 16px !important;
                border: 1px solid #7A797B !important;
                margin: 0 !important;
                background-color: white !important;

                .btn-content {
                  display: flex !important;
                  flex-direction: column !important;
                  row-gap: 4px !important;

                  .divider {
                    display: none !important;
                  }

                  .term-length {
                    margin: 0 !important;
                  }

                  .plan-price {
                    font-weight: 500 !important;
                  }
                }
              }

              .btn-offer:hover {
                background-color: white !important;
                color: #414042 !important;
              }
            }
        </style>`;
    }

    const component = document.querySelector(`#${EXTEND_COMPONENT_ID}`);
    if (component && window.Extend) {
        try {
            const extendComponent = window.Extend.buttons.instance(`#${EXTEND_COMPONENT_ID}`);
            if (extendComponent) {
                extendComponent.setActiveProduct(productId);
            } else {
                window.Extend.buttons.render(`#${EXTEND_COMPONENT_ID}`, {
                    referenceId: productId,
                });
            }

            updateWidgetStyle();

            setTimeout(() => {
                updateWidgetStyle();
            }, 500);
        } catch (e) {
            console.error(e);
        }
    }
};

const updateWidgetStyle = (): void => {
    if (isServer || !window.Extend) {
        return;
    }

    const extend = getExtendDocument();
    if (!extend) {
        return;
    }

    const head = extend.querySelector('head');
    if (head) {
        if (!head.querySelector(`#${EXTEND_HEAD_ID}`)) {
            head.innerHTML += extendCSS;

            const extendDocument = getExtendDocument();
            if (extendDocument) {
                const extendText = extendDocument.querySelector('.caption-text');
                if (extendText) {
                    extendText.textContent = 'Add a warranty from';
                }
            }
        }
    }

    isAvailable = isAvailableForProduct();
};

const getExtendDocument = (): Document | undefined => {
    if (isServer || !window.Extend) {
        return undefined;
    }

    const iframe = document.querySelector(EXTEND_IFRAME) as HTMLIFrameElement;
    if (!iframe) {
        return undefined;
    }

    return iframe.contentWindow?.document;
};

const getPlan = (): ExtendPlan | undefined => {
    if (isServer || !window.Extend) {
        return undefined;
    }

    const extend = window.Extend.buttons.instance('#extend-offer');

    return extend?.getPlanSelection();
};

const openModal = (productId: string, onClose: ExtendModalOnClose): void => {
    if (isServer || !window.Extend) {
        onClose();
        return;
    }

    window.Extend.modal.open({
        referenceId: productId,
        onClose,
    });
};

const buildExtendProductFromProductItem = (
    basket: Basket,
    item: NewProductItem,
): Product | null => {
    if (item.c_extendPlan) {
        const extendItem = basket.productItems?.find(
            ({ c_ExtendAssociation }) => c_ExtendAssociation === item.productId,
        );

        if (extendItem) {
            return {
                id: extendItem.productId,
                name: extendItem.productName,
                price: extendItem.price,
                c_imageUrl: extendItem.c_imageUrl,
                variants: [],
                c_shippingOptions: [],
            };
        }
    }

    return null;
};

const buildExtendProductFromProduct = (product: Product): Product => ({
    id: product.id,
    name: product.name,
    price: product.price,
    c_imageUrl: product.c_imageUrl,
    variants: [],
    c_shippingOptions: [],
});

const addPlan = (
    item: ProductItem | NewProductItem,
    extendPlan?: ExtendPlan,
): NewHardGoodItemWithExtend | undefined => {
    const plan = extendPlan ?? getPlan();
    if (!plan) {
        return;
    }

    item.c_extendPlan = plan;
    item.c_ExtendAssociation = getExtendProductId(plan.term, false);
};

const isAvailableForProduct = (): boolean => {
    const extend = getExtendDocument();

    return (
        isClient &&
        !!window.Extend &&
        !!extend?.querySelector('head')?.querySelector(`#${EXTEND_HEAD_ID}`)
    );
};

export const useExtendWarranty = (): ExtendWarrantyContext => {
    const preferences = useCustomPreferences([
        CustomPreference.EXTEND_ENABLED,
        CustomPreference.EXTEND_PDP_ENABLED,
        CustomPreference.EXTEND_PDP_UP_SELL_ENABLED,
        CustomPreference.EXTEND_SDK,
        CustomPreference.EXTEND_STORE_ID,
        CustomPreference.EXTEND_PRODUCTION_MODE,
    ]);

    const isPDPEnabled =
        !!preferences[CustomPreference.EXTEND_ENABLED] &&
        !!preferences[CustomPreference.EXTEND_PDP_ENABLED];
    const isPDPUpSellEnabled =
        isPDPEnabled && !!preferences[CustomPreference.EXTEND_PDP_UP_SELL_ENABLED];
    const isCartUpsellEnabled =
        !!preferences[CustomPreference.EXTEND_ENABLED] &&
        !!preferences[CustomPreference.EXTEND_CART_UP_SELL_ENABLED];

    const extendSDK = preferences[CustomPreference.EXTEND_SDK];
    const extendStore = preferences[CustomPreference.EXTEND_STORE_ID];
    const extendEnv = preferences[CustomPreference.EXTEND_PRODUCTION_MODE] ? 'production' : 'demo';

    const extendScriptStatus = useScript(extendSDK?.toString() ?? '', {
        removeOnUnmount: false,
        shouldPreventLoad: !isPDPEnabled && !isCartUpsellEnabled,
    });

    useEffect(() => {
        if (extendScriptStatus === 'ready' && typeof window.Extend !== 'undefined') {
            window.Extend.config({
                storeId: extendStore?.toString() ?? '',
                environment: extendEnv,
            });
        }
    }, [extendScriptStatus, extendStore, extendEnv]);

    return {
        isPDPEnabled,
        isPDPUpSellEnabled,
        isCartUpsellEnabled,
        addPlan,
        getPlan,
        buildExtendProductFromProductItem,
        buildExtendProductFromProduct,
        init,
        isAvailable,
        openModal,
        updateWidgetStyle,
        isSdkLoaded: extendScriptStatus === 'ready',
    };
};
